import { Box, CircularProgress, Divider, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { Upload, X } from "lucide-react";
import React from "react";
import { useDropzone } from "react-dropzone";
import { useTranslation } from "react-i18next";
import { ButtonDigited } from "../Button/Button.variants";
import { formatBytes } from "../ExercisesCard/FormatBytes";
import { ModalBasic } from "../ModalBasic";

interface UploadCertificateModalProps {
  isLoading?: boolean;
  onCancel: () => void;
  onConfirm: (file: File) => void;
  open: boolean;
}

const ACCEPTED_FILES = {
  "application/pdf": [".pdf"],
  "image/jpeg": [".jpg", ".jpeg"],
  "image/png": [".png"]
};

export const UploadCertificateModal: React.FC<UploadCertificateModalProps> = ({
  isLoading,
  onCancel,
  onConfirm,
  open
}) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const [selectedFile, setSelectedFile] = React.useState<File | null>(null);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: ACCEPTED_FILES,
    maxFiles: 1,
    multiple: false,
    onDrop: (acceptedFiles: File[]) => {
      setSelectedFile(acceptedFiles[0] ?? null);
    }
  });

  // Reset del file selezionato ad ogni apertura/chiusura della modale
  React.useEffect(() => {
    setSelectedFile(null);
  }, [open]);

  return (
    <ModalBasic
      open={ open }
      title={ "" }
      handleClose={ onCancel }
      size={ { height: "auto", maxHeight: "90vh", width: { sm: "38rem", xs: "100vw" } } }
      content={
        <Box
          sx={ {
            display: "flex",
            flexDirection: "column",
            gap: "1.5rem",
            textAlign: "center",
            width: "100%"
          } }
        >
          <Typography
            variant="h5"
            fontWeight={ 700 }
            color={ theme.customColors.textPrimary }
          >
            { t("upload_certificate_title") }
          </Typography>

          <Typography
            variant="body1"
            color={ theme.customColors.textPrimary }
          >
            { t("upload_certificate_subtitle") }
          </Typography>

          <Box
            { ...getRootProps() }
            sx={ {
              alignItems: "center",
              border: `2px dashed ${theme.customColors.backgroundPrimaryCta}`,
              borderRadius: "16px",
              cursor: "pointer",
              display: "flex",
              flexDirection: "column",
              gap: "0.5rem",
              justifyContent: "center",
              minHeight: "12rem",
              opacity: isDragActive ? 0.7 : 1,
              p: "1.5rem",
              transition: "opacity .2s",
              width: "100%"
            } }
          >
            <input { ...getInputProps() } />
            <Upload size={ 24 } color={ theme.customColors.backgroundPrimaryCta } />
            <Typography
              variant="body1"
              fontWeight={ 700 }
              color={ theme.customColors.textPrimary }
            >
              { t("file_field") }
            </Typography>
            <Typography
              variant="body2"
              color={ theme.customColors.textPrimary }
            >
              { t("upload_certificate_dropzone_hint") }
            </Typography>
          </Box>

          { selectedFile && (
            <Box
              sx={ {
                alignItems: "center",
                border: `1px solid ${theme.customColors.border}`,
                borderRadius: "8px",
                display: "flex",
                gap: "0.5rem",
                justifyContent: "space-between",
                p: "0.75rem 1rem",
                width: "100%"
              } }
            >
              <Box
                sx={ {
                  minWidth: 0,
                  textAlign: "left"
                } }
              >
                <Typography
                  variant="body2"
                  fontWeight={ 700 }
                  color={ theme.customColors.textPrimary }
                  sx={ { overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" } }
                >
                  { selectedFile.name }
                </Typography>
                <Typography
                  variant="caption"
                  color={ theme.customColors.textPrimary }
                  sx={ { opacity: 0.7 } }
                >
                  { formatBytes(selectedFile.size) }
                </Typography>
              </Box>
              <Box
                component="button"
                type="button"
                aria-label={ t("delete") }
                disabled={ isLoading }
                onClick={ () => setSelectedFile(null) }
                sx={ {
                  background: "none",
                  border: "none",
                  color: theme.customColors.textPrimary,
                  cursor: isLoading ? "default" : "pointer",
                  display: "flex",
                  p: 0
                } }
              >
                <X size={ 20 } />
              </Box>
            </Box>
          ) }

          <Divider sx={ { borderColor: theme.customColors.border } } />

          <Box
            sx={ {
              display: "flex",
              flexWrap: "wrap",
              gap: "1rem",
              justifyContent: "center"
            } }
          >
            <ButtonDigited
              typology="secondary"
              onClick={ onCancel }
              text={ t("annul") }
              disabled={ isLoading }
            />
            <ButtonDigited
              typology="primary"
              onClick={ () => selectedFile && onConfirm(selectedFile) }
              disabled={ !selectedFile || isLoading }
            >
              <>
                { isLoading && <CircularProgress size={ 16 } color="inherit" /> }
                <span>{ t("upload_button") }</span>
              </>
            </ButtonDigited>
          </Box>
        </Box>
      }
    />
  );
};
